import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { AppThemeProvider } from './ThemeModeContext';
import { AuthProvider } from './auth/AuthContext';
import { useMsal } from '@azure/msal-react';
import {
  EventType,
  InteractionType,
  type EventMessage,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { useAuth } from './auth/useAuth';
import type { AuthContextValue } from './auth/AuthContext';
import { OAUTH_PERSONA_KEY } from './auth/OAuthProviders';
import type { AccountPersona } from './api/types';
import { ErrorBoundary } from './components/ErrorBoundary';
import { routeTree } from './routeTree.gen';

// El router se crea una vez y se rehúsa entre renders. El context se inyecta vía RouterProvider.
// Tipo declaramos abajo para que beforeLoad/etc. tipa el ctx correctamente.

export interface RouterContext {
  auth: AuthContextValue;
}

const router = createRouter({
  routeTree,
  context: { auth: undefined! } as { auth: AuthContextValue },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function RouterWithAuth(): JSX.Element {
  const auth = useAuth();
  const { instance: msalInstance } = useMsal();
  const redirectHandledRef = useRef(false);
  // Mientras MSAL canjea el redirect (token + signIn contra el backend, ~varios seg) la app todavía
  // ve isAuthenticated=false y el guard rebota a /login → parpadeo confuso. Tapamos ese tramo con un
  // overlay. Arranca activo si la URL trae el `code` del redirect de Microsoft.
  const [completingRedirect, setCompletingRedirect] = useState(
    () => typeof window !== 'undefined' && (window.location.hash + window.location.search).includes('code='),
  );

  // Redirect flow de Microsoft: al volver del IdP, canjeamos el id_token contra el backend y
  // navegamos. Escuchamos dos fuentes para evitar la race de MSAL:
  //  1) el evento LOGIN_SUCCESS (si el callback ya estaba registrado cuando se emitió), y
  //  2) handleRedirectPromise() — idempotente — que devuelve el resultado aunque MsalProvider
  //     ya lo haya procesado antes de montar este componente (caso típico del race).
  // El ref garantiza canjear una sola vez. Reset en error para permitir reintento.
  useEffect(() => {
    const handleIdToken = (idToken: string | undefined | null): void => {
      if (!idToken || redirectHandledRef.current) return;
      redirectHandledRef.current = true;
      setCompletingRedirect(true); // mantené el overlay durante el canje contra el backend
      const persona = (sessionStorage.getItem(OAUTH_PERSONA_KEY) as AccountPersona | null) ?? undefined;
      sessionStorage.removeItem(OAUTH_PERSONA_KEY);
      void auth
        .signInWithProvider('Microsoft', idToken, persona)
        .then((outcome) => router.navigate({ to: outcome.isNewUser ? '/onboarding' : '/dashboard' }))
        .then(() => setCompletingRedirect(false))
        .catch(() => {
          redirectHandledRef.current = false; // permite reintentar
          setCompletingRedirect(false); // mostrá el login de nuevo (con el error)
        });
    };

    const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType !== EventType.LOGIN_SUCCESS || event.interactionType !== InteractionType.Redirect) {
        return;
      }
      handleIdToken((event.payload as AuthenticationResult | null)?.idToken);
    });

    // Fallback anti-race: captura el resultado del redirect aunque el evento ya se haya emitido.
    void msalInstance
      .handleRedirectPromise()
      .then((res) => handleIdToken(res?.idToken))
      .catch(() => setCompletingRedirect(false));

    // Safety net: nunca dejar el overlay clavado si el redirect no produjo token.
    const safety = window.setTimeout(() => setCompletingRedirect(false), 12_000);

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
      window.clearTimeout(safety);
    };
  }, [msalInstance, auth]);

  // Pasar `context={{ auth }}` mantiene el context del router fresco en cada render, PERO TanStack
  // Router NO re-ejecuta los `beforeLoad` guards solo porque el context cambie — hay que invalidar
  // explícitamente. Sin esto, tras un login el `navigate('/dashboard')` corre con el context viejo
  // (isAuthenticated=false), el guard de `_authenticated` rebota a `/login`, y como nada vuelve a
  // correr los guards, el usuario queda clavado en /login pese a tener token válido. Invalidar al
  // cambiar `isAuthenticated` fuerza a los guards a re-evaluar con el auth nuevo (login y logout).
  useEffect(() => {
    void router.invalidate();
  }, [auth.isAuthenticated]);

  return (
    <>
      <RouterProvider router={router} context={{ auth }} />
      {completingRedirect ? <RedirectLoadingOverlay /> : null}
    </>
  );
}

/** Overlay full-screen que tapa el parpadeo del login mientras MSAL canjea el redirect. */
function RedirectLoadingOverlay(): JSX.Element {
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (t) => t.zIndex.modal + 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Completando ingreso…
      </Typography>
    </Box>
  );
}

export function App(): JSX.Element {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
    [],
  );

  return (
    <ErrorBoundary>
      <AppThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterWithAuth />
          </AuthProvider>
        </QueryClientProvider>
      </AppThemeProvider>
    </ErrorBoundary>
  );
}
